<script setup lang="ts">
import { computed, nextTick, shallowRef } from 'vue';
import { CompanySide } from '@/modules/user-accounts/types/user-accounts';
import CounterpartySearch from '@/modules/user-accounts/components/CounterpartySearch.vue';
import { formatCompanyName } from '@/modules/user-accounts/helpers/user-accounts';
import { CompanyInfo } from '@/modules/common/models';

type DisplayMode = 'table' | 'chip';

const props = withDefaults(
  defineProps<{
    counterparties: CompanyInfo[];
    mode: DisplayMode;
    errorMessages?: string | string[];
    // passthrough props to CounterpartySearch
    label?: string;
    excludeCompanyId?: string;
    autofocus?: boolean;
    disabled?: boolean;
    clearable?: boolean;
    dense?: boolean;
    placeholder?: string;
    prependItem?: CompanyInfo;
    side?: CompanySide;
    sponsored?: boolean;
    includeSponsored?: boolean;
    onlyBilateralSettlementOnAurora?: boolean;
    maxHeight?: string;
  }>(),
  {
    counterparties: () => [],
    side: 'ALL',
    errorMessages: () => [],
    label: undefined,
    excludeCompanyId: undefined,
    placeholder: undefined,
    prependItem: undefined,
    maxHeight: '40vh',
  }
);

const emit = defineEmits<{
  (event: 'blur'): void;
  (event: 'add' | 'remove', item: CompanyInfo): void;
  (event: 'update:counterparties', items: CompanyInfo[]): void;
}>();

const selectedSearchItem = shallowRef<CompanyInfo | null>(null);
const localErrorMsg = shallowRef<string | null>(null);

const selectedCompanyIds = computed(() => props.counterparties.map(({ companyId }) => companyId));

async function onCounterpartyChange(newCounterparty: CompanyInfo | null): Promise<void> {
  localErrorMsg.value = null;

  if (newCounterparty === null) {
    // user cleared the search field
    return;
  }

  selectedSearchItem.value = newCounterparty;

  if (selectedCompanyIds.value.includes(newCounterparty.companyId)) {
    localErrorMsg.value = 'counterparty already in list.';
    return;
  }

  const updatedCounterparties = [...props.counterparties, newCounterparty];
  emit('update:counterparties', updatedCounterparties);
  emit('add', newCounterparty);

  // get model and validator ready for the next counterparty
  await nextTick();
  selectedSearchItem.value = null;
}

function removeCounterparty(item: CompanyInfo): void {
  const updatedCounterparties = props.counterparties.filter((c) => c.companyId !== item.companyId);
  emit('update:counterparties', updatedCounterparties);
  emit('remove', item);
}
</script>

<template>
  <div>
    <CounterpartySearch
      v-model="selectedSearchItem"
      :autofocus="autofocus"
      :clearable="clearable"
      :dense="dense"
      :disabled="disabled"
      :error-messages="localErrorMsg || errorMessages"
      :exclude-company-id="excludeCompanyId"
      :exclude-company-ids="selectedCompanyIds"
      :include-sponsored="includeSponsored"
      :label="label"
      :only-bilateral-settlement-on-aurora="onlyBilateralSettlementOnAurora"
      :placeholder="placeholder"
      :prepend-item="prependItem"
      :side="side"
      :sponsored="sponsored"
      @blur="emit('blur')"
      @change="onCounterpartyChange"
      @input="onCounterpartyChange"
    />

    <template v-if="counterparties.length">
      <div v-if="mode === 'table'" class="table-container mt-4" :style="{ maxHeight }">
        <div>
          <VDataTable
            disable-filtering
            disable-pagination
            disable-sort
            fixed-header
            hide-default-footer
            item-key="companyId"
            :items="counterparties"
            width="100%"
          >
            <template #item="{ item }">
              <tr>
                <td>
                  {{ formatCompanyName(item) }}
                </td>
                <td class="text-right">
                  <VBtn class="icon-action" icon @click="removeCounterparty(item)">
                    <VIcon size="24">mdi-close-circle</VIcon>
                  </VBtn>
                </td>
              </tr>
            </template>
          </VDataTable>
        </div>
      </div>
      <div v-if="mode === 'chip'" class="mt-n4">
        <VChip
          v-for="item in counterparties"
          :key="item.companyId"
          close
          :disabled="disabled"
          small
          @click="removeCounterparty(item)"
          @click:close="removeCounterparty(item)"
        >
          {{ item.displayId }}
        </VChip>
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.icon-action {
  opacity: 0.4;
  transition: opacity linear 0.15s;
}

.icon-action:hover {
  opacity: 1;
}

::v-deep {
  .v-data-table,
  .v-data-table > div {
    width: 100%;
    display: flex;
  }
}

.table-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.table-container > div {
  display: flex;
  overflow: hidden;
}
</style>
